"use client";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Custom Card component to replace shadcn/ui Card
const Card = ({ children, className = "" }) => {
  return <div className={`bg-white rounded-lg ${className}`}>{children}</div>;
};

const CardHeader = ({ children, className = "" }) => {
  return <div className={`${className}`}>{children}</div>;
};

const CardContent = ({ children, className = "" }) => {
  return <div className={`${className}`}>{children}</div>;
};

// Animated section component
const AnimatedSection = ({ children }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

export default function MeritCriteriaPage() {
  const navigate = useNavigate();

  const goBack = () => {
    navigate("/");
  };

  return (
    <div className="container mx-auto px-4 py-6 md:py-4">
      <button
        onClick={goBack}
        className="mb-4 flex items-center gap-3 text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg py-3 px-6 shadow-md hover:shadow-lg"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-6 w-6"
        >
          <path d="m12 19-7-7 7-7" />
          <path d="M19 12H5" />
        </svg>
        <span className="font-medium text-lg">Go Back</span>
      </button>

      <AnimatedSection>
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardHeader className="bg-[#1e3a8a] text-white p-4 md:p-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex flex-col md:flex-row items-center md:space-x-4 text-center md:text-left">
                <img
                  src="/logo.png"
                  alt="NASTP Logo"
                  width={80}
                  height={80}
                  className="rounded-full mb-2 md:mb-0"
                />
                <div>
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight">
                    NASTP INSTITUTE OF INFORMATION
                  </h1>
                  <h2 className="text-lg md:text-xl lg:text-2xl font-bold tracking-tight">
                    TECHNOLOGY (NIIT), LAHORE
                  </h2>
                </div>
              </div>
              <img
                src="/air-logo-transpatrent.png"
                alt="Air University Logo"
                width={100}
                height={100}
                className="mt-2 md:mt-0"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="bg-gray-100 py-6 px-4 md:py-8 md:px-6">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-gray-800">
                Merit Calculation Matrix
              </h2>
            </div>

            <div className="p-4 md:p-6">
              <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                <div className="flex flex-col md:flex-row">
                  <div className="bg-blue-700 text-white rounded-t-lg md:rounded-tr-none md:rounded-l-lg p-3 md:p-4 md:w-4/5 flex items-center">
                    <span className="text-lg md:text-xl lg:text-2xl font-semibold">
                      Matric / O-Level or Equivalent Qualification
                    </span>
                  </div>
                  <div className="bg-blue-700 text-white rounded-b-lg md:rounded-bl-none md:rounded-r-lg p-3 md:p-4 md:w-1/5 flex items-center justify-center">
                    <span className="text-lg md:text-xl lg:text-2xl font-bold">
                      15%
                    </span>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row">
                  <div className="bg-blue-700 text-white rounded-t-lg md:rounded-tr-none md:rounded-l-lg p-3 md:p-4 md:w-4/5 flex items-center">
                    <span className="text-lg md:text-xl lg:text-2xl font-semibold">
                      FSc (Part-1) / AS-Level or Equivalent Qualification
                    </span>
                  </div>
                  <div className="bg-blue-700 text-white rounded-b-lg md:rounded-bl-none md:rounded-r-lg p-3 md:p-4 md:w-1/5 flex items-center justify-center">
                    <span className="text-lg md:text-xl lg:text-2xl font-bold">
                      35%
                    </span>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row">
                  <div className="bg-blue-700 text-white rounded-t-lg md:rounded-tr-none md:rounded-l-lg p-3 md:p-4 md:w-4/5 flex items-center">
                    <span className="text-lg md:text-xl lg:text-2xl font-semibold">
                      Admission Test
                    </span>
                  </div>
                  <div className="bg-blue-700 text-white rounded-b-lg md:rounded-bl-none md:rounded-r-lg p-3 md:p-4 md:w-1/5 flex items-center justify-center">
                    <span className="text-lg md:text-xl lg:text-2xl font-bold">
                      50%
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-8 md:mt-10">
                <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 mb-3 md:mb-4">
                  Acceptable Admission Tests
                </h3>

                <div className="space-y-2 md:space-y-3">
                  <div className="bg-blue-700 text-white rounded-lg p-3 md:p-4">
                    <span className="text-base md:text-lg lg:text-xl font-semibold">
                      NIIT Admission Test
                    </span>
                  </div>
                  <div className="bg-blue-700 text-white rounded-lg p-3 md:p-4">
                    <span className="text-base md:text-lg lg:text-xl font-semibold">
                      NTS
                    </span>
                  </div>
                  <div className="bg-blue-700 text-white rounded-lg p-3 md:p-4">
                    <span className="text-base md:text-lg lg:text-xl font-semibold">
                      SAT
                    </span>
                  </div>
                </div>

                <p className="mt-6 md:mt-8 text-base md:text-lg lg:text-xl text-gray-700">
                  IBCC equivalence is required in case of foreign qualification
                </p>
              </div>

              <div className="mt-6 md:mt-8 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4 text-gray-700">
                <div className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 md:h-6 md:w-6 text-blue-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="text-base md:text-lg">
                    69-Abid Majeed Road, Lahore Cantt
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 md:h-6 md:w-6 text-blue-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span className="text-base md:text-lg">0320-9420163</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 md:h-6 md:w-6 text-blue-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span className="text-base md:text-lg">042-36666033</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </AnimatedSection>
    </div>
  );
}
